// /english/:slug/practice — the live session.
//
// One column, like every other page: controls, then the conversation, then the
// phrase scoreboard and corrections beneath it. It used to be three fixed
// columns (256 px + 320 px of chrome), which squeezed the transcript to 68 px on
// a 500 px viewport — unusable on anything smaller than a laptop.
//
// The story comes from the URL, not from a picker. You arrive here from a lesson
// page having chosen what to practise, so a second selector on this screen would
// be a second source of truth — and it had to be disabled mid-session anyway,
// because you cannot switch story with a socket open.
//
// Admin only. The way back out is CoachShell's header link — there is no shared
// site nav anywhere any more.

import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { fetchSpend, fetchTopic, resetTopic } from '../services/englishApi';
import useVoiceLive from '../hooks/useVoiceLive';
import CoachShell from '../components/english/CoachShell';
import CorrectionFeed from '../components/english/CorrectionFeed.jsx';
import LiveTranscript from '../components/english/LiveTranscript.jsx';
import MicOrb from '../components/english/MicOrb.jsx';
import PhraseChecklist from '../components/english/PhraseChecklist.jsx';
import SessionHistory from '../components/english/SessionHistory.jsx';
import SessionSummary from '../components/english/SessionSummary.jsx';
import SpendMeter from '../components/english/SpendMeter.jsx';

export default function EnglishCoachPage() {
  const { slug } = useParams();
  const [topic, setTopic] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [focus, setFocus] = useState(null);
  const [turns, setTurns] = useState([]);
  const [corrections, setCorrections] = useState([]);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [nudgeText, setNudgeText] = useState('');
  const [spend, setSpend] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  // --- data loading ---------------------------------------------------------
  const loadSpend = useCallback(async () => {
    try {
      setSpend(await fetchSpend());
    } catch {
      /* the meter is informational — a failure here must not block practice */
    }
  }, []);

  useEffect(() => { loadSpend(); }, [loadSpend]);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    fetchTopic(slug)
      .then((data) => {
        if (cancelled) return;
        setTopic(data);
        setCoverage(data.coverage);
      })
      .catch(() => !cancelled && setError('Could not load that story.'));
    return () => { cancelled = true; };
  }, [slug]);

  // --- the live session -----------------------------------------------------
  const voice = useVoiceLive({
    slug,
    onCoverage: (msg) => setCoverage(msg),
    onFocus: (phrase) => setFocus(phrase),
    onCorrection: (c) => setCorrections((prev) => [...prev, c]),
    onTurn: (t) => setTurns((prev) => [...prev, t]),
    onSummary: (s) => { setSummary(s); loadSpend(); },
    // Transient, not an error — clears itself so it cannot linger over a session.
    onNotice: (text) => { setNotice(text); setTimeout(() => setNotice(null), 6000); },
    onError: (detail) => setError(detail),
  });

  const startSession = useCallback(() => {
    // A fresh session starts from a clean screen; the phrase counts persist
    // server-side, so only the per-session panels reset.
    setTurns([]);
    setCorrections([]);
    setSummary(null);
    setFocus(null);
    setError(null);
    voice.start();
  }, [voice]);

  const stopSession = useCallback(() => {
    voice.stop();
    setFocus(null);
    // The backend finishes writing the session record as the socket tears down,
    // which can land after the first read — retry a few times rather than showing
    // a total that is missing the session just finished.
    [0, 800, 2000, 4000].forEach((d) => setTimeout(loadSpend, d));
  }, [voice, loadSpend]);

  const onReset = useCallback(async () => {
    if (!slug) return;
    try {
      const data = await resetTopic(slug);
      setCoverage(data.coverage);
    } catch {
      setError('Could not reset this story.');
    }
  }, [slug]);

  const sendNudge = useCallback(
    (e) => {
      e.preventDefault();
      if (!nudgeText.trim()) return;
      voice.nudge(nudgeText);
      setNudgeText('');
    },
    [nudgeText, voice],
  );

  return (
    <CoachShell
      back={{ to: `/english/${encodeURIComponent(slug || '')}`, label: 'Back to the lesson' }}
      title={topic?.title || (error ? 'No such story' : 'Loading…')}
      subtitle={topic?.theme}
    >
      <div className="space-y-4">
        {/* Controls first: the mic is the reason you are on this page. */}
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
          <MicOrb
            status={voice.status}
            active={voice.active}
            elapsed={voice.elapsed}
            onStart={startSession}
            onStop={stopSession}
            onFinish={voice.finish}
            // Nothing to practise if the story failed to load — starting would
            // open a socket only for the server to close it with 4404.
            disabled={!topic}
          />
        </div>

        {notice && (
          <div className="rounded-xl border border-slate-800 bg-slate-900/60 px-4 py-2 text-xs text-slate-400">
            {notice}
          </div>
        )}

        {error && (
          <div className="rounded-xl border border-rose-900/60 bg-rose-950/40 px-4 py-2 text-xs text-rose-300">
            {error}
            <button
              type="button"
              onClick={() => setError(null)}
              className="ml-2 underline underline-offset-2"
            >
              dismiss
            </button>
          </div>
        )}

        <LiveTranscript
          turns={turns}
          userPartial={voice.userPartial}
          coachPartial={voice.coachPartial}
          empty={
            voice.active
              ? 'Listening — say something to get started.'
              : 'Press the button and start talking. The coach will drill this story’s phrases with you.'
          }
        />

        {voice.active && (
          <form onSubmit={sendNudge}>
            <input
              value={nudgeText}
              onChange={(e) => setNudgeText(e.target.value)}
              placeholder="Type instead of speaking (optional)…"
              className="w-full rounded-lg border border-slate-800 bg-slate-900 px-3 py-2 text-sm text-slate-200 placeholder:text-slate-600 focus:border-sky-600 focus:outline-none"
            />
          </form>
        )}

        <SessionSummary summary={summary} onDismiss={() => setSummary(null)} />

        {/* The scoreboard, below the conversation rather than beside it — and
            with the phrases concealed, because reading them off the screen while
            the coach asks for them is not practice. The lesson page shows the
            full list; that is where you study before you speak. */}
        <PhraseChecklist
          coverage={coverage}
          focus={focus}
          onReset={onReset}
          resetDisabled={voice.active}
          concealed
        />

        <CorrectionFeed corrections={corrections} />

        <SpendMeter
          spend={spend}
          liveCost={voice.sessionCost ?? summary?.cost_usd}
          active={voice.active}
          onOpenHistory={() => setShowHistory(true)}
        />

        {showHistory && (
          <SessionHistory slug={null} onClose={() => setShowHistory(false)} />
        )}
      </div>
    </CoachShell>
  );
}
